// Linter for the Search page "Raw JSON" editing mode. Validates that the pasted
// text is (1) valid JSON, (2) the right shape (viewMode + card/title/price objects),
// and (3) real CSS — every leaf is checked with the browser's own CSS.supports() so
// merchants can set anything (boxShadow, filter, transition, …) but typos are caught.
// It mirrors the storefront's apply pipeline exactly: `font`/`fontSize` are the two
// keys the builders transform (fontFamily / percent); every other numeric value
// becomes a px length, matching importantStyleRef.

import { ISearchPageConfig } from './searchPageConfig';

export interface ILintResult {
    valid: boolean;
    errors: string[];
    warnings: string[];
    parsed?: ISearchPageConfig;
}

const STYLE_SECTIONS = ['card', 'cardHover', 'title', 'price', 'image', 'container', 'paginatorContainer', 'paginatorCell', 'breadcrumbContainer', 'breadcrumbItem', 'searchBar', 'facetPanel', 'facetHeading', 'addToCartButton', 'addToCartLabel', 'categoryChip', 'manufacturerChip'] as const;

// The keys the storefront transforms itself rather than emitting as literal CSS.
const APP_KEYS: Record<string, (v: string | number) => { prop: string; val: string }> = {
    font: (v) => ({ prop: 'font-family', val: String(v) }),
    fontSize: (v) => ({ prop: 'font-size', val: `${v}%` }),
};

// Patterns that must never appear in merchant CSS that renders to shoppers: script
// URLs, IE expressions, @import, and external url() references (exfiltration/tracking).
const DANGEROUS = /(expression\s*\(|javascript\s*:|@import|url\s*\(|<|>)/i;

const kebab = (key: string) => key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

const cssSupports = (prop: string, val: string): boolean => {
    try {
        return typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports(prop, val);
    } catch {
        return false;
    }
};

export function lintSearchPageConfig(raw: string): ILintResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return { valid: false, errors: [`Invalid JSON: ${(e as Error).message}`], warnings: [] };
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { valid: false, errors: ['The top-level value must be a JSON object.'], warnings: [] };
    }
    const obj = parsed as Record<string, unknown>;

    if (obj.viewMode !== undefined && obj.viewMode !== 'rows' && obj.viewMode !== 'cards') {
        errors.push(`viewMode must be "rows" or "cards" (got ${JSON.stringify(obj.viewMode)}).`);
    }
    if (obj.cardGap !== undefined && (typeof obj.cardGap !== 'number' || Number.isNaN(obj.cardGap) || obj.cardGap < 0)) {
        errors.push(`cardGap must be a non-negative number (px).`);
    }
    if (obj.stickyControls !== undefined && typeof obj.stickyControls !== 'boolean') {
        errors.push(`stickyControls must be a boolean.`);
    }
    // facetSidebar is behavioral config (not a CSS style section) — validate its own shape.
    if (obj.facetSidebar !== undefined && obj.facetSidebar !== null) {
        const fs = obj.facetSidebar;
        if (typeof fs !== 'object' || Array.isArray(fs)) {
            errors.push(`facetSidebar must be an object ({ enabled, layout, priceFilter }).`);
        } else {
            const f = fs as Record<string, unknown>;
            if (f.enabled !== undefined && typeof f.enabled !== 'boolean') errors.push(`facetSidebar.enabled must be a boolean.`);
            if (f.priceFilter !== undefined && typeof f.priceFilter !== 'boolean') errors.push(`facetSidebar.priceFilter must be a boolean.`);
            if (f.layout !== undefined && f.layout !== 'panel' && f.layout !== 'inline') errors.push(`facetSidebar.layout must be "panel" or "inline".`);
        }
    }
    for (const key of Object.keys(obj)) {
        if (key !== 'viewMode' && key !== 'cardGap' && key !== 'facetSidebar' && key !== 'stickyControls' && !STYLE_SECTIONS.includes(key as (typeof STYLE_SECTIONS)[number])) {
            warnings.push(`Unknown key "${key}". The storefront only reads viewMode, cardGap, facetSidebar, stickyControls, card, title, price, image, container, paginatorContainer, paginatorCell, breadcrumbContainer, breadcrumbItem, searchBar, facetPanel, facetHeading, addToCartButton, addToCartLabel, categoryChip, manufacturerChip and cardHover.`);
        }
    }

    for (const section of STYLE_SECTIONS) {
        const style = obj[section];
        if (style === undefined || style === null) continue;
        if (typeof style !== 'object' || Array.isArray(style)) {
            errors.push(`"${section}" must be an object of CSS properties.`);
            continue;
        }
        for (const [key, value] of Object.entries(style as Record<string, unknown>)) {
            if (value === undefined || value === null) continue;
            if (typeof value !== 'string' && typeof value !== 'number') {
                errors.push(`${section}.${key} must be a string or number (got ${typeof value}).`);
                continue;
            }
            if (DANGEROUS.test(key) || DANGEROUS.test(String(value))) {
                errors.push(`${section}.${key}: value not allowed (url(), @import, javascript:, expression() and angle brackets are blocked).`);
                continue;
            }
            const app = APP_KEYS[key];
            // Mirror the apply pipeline: app keys have their own transform; every other
            // bare number becomes a px length exactly as importantStyleRef emits it.
            const prop = app ? app(value).prop : kebab(key);
            const val = app ? app(value).val : (typeof value === 'number' ? `${value}px` : value);
            if (!cssSupports(prop, val)) {
                errors.push(`${section}.${key}: "${val}" is not a valid value for CSS "${prop}".`);
            }
        }
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
        parsed: errors.length === 0 ? (parsed as ISearchPageConfig) : undefined,
    };
}
